<script setup>
import { computed } from 'vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  sectionId: { type: String, required: true },
  title: { type: String, required: true },
  description: { type: String, default: '' },
  icon: { type: String, default: 'i-lucide-layout-dashboard' },
  isLoading: { type: Boolean, default: false },
  isEmpty: { type: Boolean, default: false },
  isError: { type: Boolean, default: false },
  isUnauthorized: { type: Boolean, default: false },
  errorTitle: { type: String, default: '' },
  errorDescription: { type: String, default: '' },
  emptyTitle: { type: String, default: '' },
  emptyDescription: { type: String, default: '' },
  unauthorizedTitle: { type: String, default: '' },
  unauthorizedDescription: { type: String, default: '' },
  retryLabel: { type: String, default: 'Tentar novamente' },
});

const emit = defineEmits(['retry']);

const stateId = computed(() => `visitas-operational-${props.sectionId}`);
const titleId = computed(() => `${stateId.value}-title`);

const handleRetry = event => {
  emit('retry', event);
};
</script>

<template>
  <section :data-section-id="sectionId" class="grid min-w-0 gap-4">
    <LaisPermissionState
      v-if="isUnauthorized"
      variant="denied"
      :title="unauthorizedTitle"
      :description="unauthorizedDescription"
    />

    <template v-else>
      <div
        class="flex flex-col gap-3 border-b border-lais-border pb-4 md:flex-row md:items-center md:justify-between"
      >
        <div class="flex min-w-0 gap-3">
          <div
            class="grid size-11 shrink-0 place-items-center rounded-lais border border-lais-border-soft bg-lais-bg-soft text-lais-primary"
          >
            <span class="size-5" :class="icon" aria-hidden="true" />
          </div>
          <div class="min-w-0">
            <h2 :id="titleId" class="text-[16px] font-semibold text-lais-text">
              {{ title }}
            </h2>
            <p
              v-if="description"
              class="mt-1 max-w-[680px] text-[13px] leading-relaxed text-lais-text-muted"
            >
              {{ description }}
            </p>
          </div>
        </div>
        <div
          v-if="$slots.actions"
          class="flex flex-wrap items-center gap-2 md:justify-end"
        >
          <slot name="actions" />
        </div>
      </div>

      <div
        v-if="isLoading"
        role="status"
        :aria-labelledby="titleId"
        class="grid min-h-[220px] place-items-center rounded-lais border border-lais-border bg-lais-bg-card"
      >
        <Spinner :size="32" class="text-lais-primary" />
      </div>

      <LaisErrorState
        v-else-if="isError"
        :title="errorTitle"
        :description="errorDescription"
        :action-label="retryLabel"
        show-action
        @action="handleRetry"
      />

      <EmptyStateLayout
        v-else-if="isEmpty"
        :title="emptyTitle"
        :subtitle="emptyDescription"
        :show-backdrop="false"
      >
        <template v-if="$slots['empty-actions']" #actions>
          <slot name="empty-actions" />
        </template>
      </EmptyStateLayout>

      <slot v-else />
    </template>
  </section>
</template>
